import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import * as adminService from "@/shared/api/services/adminService";
import * as providerService from "@/shared/api/services/providerService";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getMerchantName } from "@/shared/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Search,
  Calendar as CalendarIcon,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Globe,
  Filter,
  X,
} from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";

// This page uses the default admin layout

export default function AdminGlobalTransactions() {
  const { t, language } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    engine: "all",
    reseller: "all",
    merchant: "all",
    provider: "all",
    status: "all",
    type: "all",
    dateFrom: null,
    dateTo: null,
  });
  const [selectedTx, setSelectedTx] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const { data: qrCodes = [], isLoading, refetch, error } = useQuery({
    queryKey: ["all-qr-codes"],
    queryFn: () => qrCodeService.getAllQRCodes(),
  });

  // Debug: Log qr-codes and errors
  useEffect(() => {
    if (error) {
      console.error("Error fetching qr-codes:", error);
    }
    if (qrCodes) {
      console.log("QR codes received:", qrCodes.length, qrCodes);
    }
  }, [qrCodes, error]);

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  // Filter qr-codes
  const filteredQRCodes = useMemo(() => {
    return qrCodes.filter((qr) => {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (
          !qr.payment_id?.toLowerCase().includes(search) &&
          !qr.reference?.toLowerCase().includes(search) &&
          !qr.merchant?.name?.toLowerCase().includes(search) &&
          !qr.merchant?.code?.toLowerCase().includes(search) &&
          !qr.client_bank_name?.toLowerCase().includes(search)
        ) {
          return false;
        }
      }
      if (filters.merchant !== "all" && qr.merchant_id !== filters.merchant) return false;
      if (filters.provider !== "all" && qr.client_bank_name !== filters.provider) return false;
      if (filters.status !== "all" && qr.status !== filters.status) return false;
      if (filters.reseller !== "all") {
        const resellerId = qr.merchant?.createdByResellerId;
        if (filters.reseller !== resellerId) return false;
      }
      if (filters.dateFrom) {
        const qrDate = new Date(qr.createdAt);
        if (qrDate < filters.dateFrom) return false;
      }
      if (filters.dateTo) {
        const qrDate = new Date(qr.createdAt);
        if (qrDate > filters.dateTo) return false;
      }
      return true;
    });
  }, [qrCodes, filters]);

  const paginatedQRCodes = filteredQRCodes.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredQRCodes.length / pageSize);

  const resetFilters = () => {
    setFilters({
      search: "",
      engine: "all",
      reseller: "all",
      merchant: "all",
      provider: "all",
      status: "all",
      type: "all",
      dateFrom: null,
      dateTo: null,
    });
    setPage(0);
  };

  // Helper to translate transaction type
  const getTransactionTypeLabel = (type) => {
    const typeUpper = (type || '').toUpperCase();
    if (typeUpper === "DEPOSIT") {
      return t("deposit");
    } else if (typeUpper === "WITHDRAWAL") {
      return t("withdrawal");
    }
    return type;
  };

  const handleExport = () => {
    const headers = [t("paymentId"), t("reference"), t("merchant"), t("reseller"), t("provider"), t("amount"), t("status"), t("date")];
    const rows = filteredQRCodes.map((qr) => [
      qr.payment_id,
      qr.reference || "",
      qr.merchant?.name || qr.merchant?.code || "",
      qr.merchant?.createdByResellerId ? (qr.reseller?.name || "") : "Direct",
      qr.client_bank_name || "",
      (qr.amount).toString(),
      qr.status,
      qr.createdAt,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `global-qr-codes-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // Find related entities for selected qr-code
  const selectedMerchant = selectedTx ? merchants.find(m => m.id === selectedTx.merchant_id) : null;
  const selectedReseller = selectedTx?.reseller || null;
  const selectedEngine = null; // QR codes don't have engine relation

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => v && v !== "all" && k !== "search").length;

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">{t("errorLoadingTransactions")}</p>
          <p className="text-red-600 text-sm mt-1">{error.message || t("pleaseCheckConnection")}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Globe className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("globalTransactions")}</h1>
            <p className="text-slate-500">
              {isLoading ? t("loading") : `${filteredQRCodes.length.toLocaleString()} ${t("transactionsAcrossEntities")}`}
              {!isLoading && qrCodes.length > 0 && ` (${qrCodes.length} ${t("total")})`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t("refresh")}
          </Button>
          <Button variant="outline" onClick={handleExport} className="gap-2">
            <Download className="h-4 w-4" />
            {t("export")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="font-medium text-slate-700">{t("filters")}</span>
            {activeFiltersCount > 0 && (
              <Badge className="bg-blue-100 text-blue-700">{activeFiltersCount} {t("activeFilters")}</Badge>
            )}
          </div>
          {activeFiltersCount > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-1 text-slate-500">
              <X className="h-4 w-4" />
              {t("clearAll")}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative lg:col-span-2 xl:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("searchIdRefMerchant")}
              value={filters.search}
              onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(0); }}
              className="pl-10"
            />
          </div>

          {/* Engine Filter */}
          <Select value={filters.engine} onValueChange={(v) => { setFilters({ ...filters, engine: v }); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder={t("allEngines")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allEngines")}</SelectItem>
              {engines.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Reseller Filter */}
          <Select value={filters.reseller} onValueChange={(v) => { setFilters({ ...filters, reseller: v }); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder={t("allResellers")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allResellers")}</SelectItem>
              {resellers.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Merchant Filter */}
          <Select value={filters.merchant} onValueChange={(v) => { setFilters({ ...filters, merchant: v }); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder={t("allMerchants")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allMerchants")}</SelectItem>
              {merchants.map((m) => (
                <SelectItem key={m.id} value={m.id}>{getMerchantName(m, language, t)} ({m.code})</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Provider Filter - using client_bank_name */}
          <Select value={filters.provider} onValueChange={(v) => { setFilters({ ...filters, provider: v }); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder={t("allProviders")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allProviders")}</SelectItem>
              {Array.from(new Set(qrCodes.map(qr => qr.client_bank_name).filter(Boolean))).map((bankName) => (
                <SelectItem key={bankName} value={bankName}>{bankName}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={filters.status} onValueChange={(v) => { setFilters({ ...filters, status: v }); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder={t("allStatus")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatus")}</SelectItem>
              <SelectItem value="PENDING">{t("pending")}</SelectItem>
              <SelectItem value="PROCESSING">{t("processing")}</SelectItem>
              <SelectItem value="SUCCESS">{t("success")}</SelectItem>
              <SelectItem value="FAILED">{t("failed")}</SelectItem>
              <SelectItem value="EXPIRED">{t("expired")}</SelectItem>
              <SelectItem value="CANCELLED">{t("cancelled")}</SelectItem>
            </SelectContent>
          </Select>

          {/* Date From */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.dateFrom ? format(filters.dateFrom, "MMM d, yyyy") : t("fromDate")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filters.dateFrom}
                onSelect={(date) => { setFilters({ ...filters, dateFrom: date }); setPage(0); }}
              />
            </PopoverContent>
          </Popover>

          {/* Date To */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.dateTo ? format(filters.dateTo, "MMM d, yyyy") : t("toDate")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filters.dateTo}
                onSelect={(date) => { setFilters({ ...filters, dateTo: date }); setPage(0); }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* QR Codes Table */}
      <AdminTransactionTable
        transactions={paginatedQRCodes}
        isLoading={isLoading}
        onView={setSelectedTx}
        showMerchant={true}
        showReseller={true}
        showEngine={false}
        merchants={merchants}
        resellers={resellers}
        engines={engines}
        isQRCode={true}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, filteredQRCodes.length)} {t("of")} {filteredQRCodes.length.toLocaleString()} {t("transactions")}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        engine={selectedEngine}
        reseller={selectedReseller}
        merchant={selectedMerchant}
        isQRCode={true}
      />
    </div>
  );
}